import type { ShowResultsMessage } from '@ilteducation/quizter-types';

type Result = ShowResultsMessage['payload']['results'][number];

/**
 * Adds leaderboard positions to results based on current and previous scores
 * Handles tied positions: players with the same score get the same position number
 */
export const addLeaderboardPositions = (results: Result[]) => {
  const withPrevScores = results.map((r) => ({
    ...r,
    previousScore: r.totalPoint - (r.lastQuestionPoint || 0),
  }));

  // Calculate previous positions
  const sortedByPrev = [...withPrevScores].sort((a, b) => b.previousScore - a.previousScore);
  const previousPositions = new Map<string, number>();
  let prevPosition = 1;

  for (let i = 0; i < sortedByPrev.length; i++) {
    const current = sortedByPrev[i];
    const prev = sortedByPrev[i - 1];

    if (i > 0 && current.previousScore === prev.previousScore) {
      // Same score as previous player - share position
      const prevPlayerPosition = previousPositions.get(prev.player.toString())!;
      previousPositions.set(current.player.toString(), prevPlayerPosition);
    } else {
      // Different score - new position (skip positions for tied players)
      prevPosition = i + 1;
      previousPositions.set(current.player.toString(), prevPosition);
    }
  }

  // Calculate current positions
  const sortedByCurrent = [...withPrevScores].sort((a, b) => b.totalPoint - a.totalPoint);
  let currentPosition = 1;

  return sortedByCurrent.map((r, i) => {
    const prev = sortedByCurrent[i - 1];
    if (i === 0 || r.totalPoint !== prev.totalPoint) {
      currentPosition = i + 1;
    }

    return {
      ...r,
      currentPosition,
      previousPosition: previousPositions.get(r.player.toString()),
    };
  });
};

/**
 * Groups players by their position for display purposes
 * Used by LeaderBoard to show tied players together
 */
export const groupByPosition = (results: ReturnType<typeof addLeaderboardPositions>) => {
  const groups = [];
  let i = 0;

  while (i < results.length) {
    const currentPlayer = results[i];
    const group = {
      position: currentPlayer.currentPosition,
      score: currentPlayer.totalPoint,
      lastQuestionPoints: currentPlayer.lastQuestionPoint,
      previousPosition: currentPlayer.previousPosition,
      players: [currentPlayer],
    };

    // Collect all players with same position
    let j = i + 1;
    while (j < results.length && results[j].currentPosition === currentPlayer.currentPosition) {
      group.players.push(results[j]);
      j++;
    }

    groups.push(group);
    i = j;
  }

  return groups;
};
